/**
 * Tenant Service
 * API service for tenant management
 */

import { apiClient } from './api/client';

// Types
export interface Tenant {
    id: string;
    name: string;
    slug: string;
    description?: string;
    admin_email: string;
    is_active: boolean;
    api_rate_limit: number;
    public_key: string;
    settings?: Record<string, unknown>;
    token_count?: number;
    created_at: string;
    updated_at: string;
}

export interface TenantListResponse {
    count: number;
    results: Tenant[];
}

export interface PortalUserPayload {
    username: string;
    password: string;
    email?: string;
    permission_group_slug: 'sys-viewer' | 'sys-editor' | 'sys-admin';
}

export interface TenantRegistrationRequest {
    name: string;
    slug: string;
    admin_email: string;
    description?: string;
    api_rate_limit?: number;
    portal_user?: PortalUserPayload;
}

export interface TenantRegistrationApiToken {
    id: string;
    key: string;
    name: string;
    created_at: string;
    permission_group_slug?: string | null;
}

export interface PortalUserConfirmation {
    username: string;
    email: string;
    tenant_slug: string;
    permission_group_slug: string;
}

export interface TenantRegistrationResponse {
    message: string;
    tenant: Tenant;
    api_token: TenantRegistrationApiToken;
    portal_user?: PortalUserConfirmation | null;
}

export interface TenantUpdateRequest {
    name?: string;
    slug?: string;
    description?: string;
    admin_email?: string;
    is_active?: boolean;
    api_rate_limit?: number;
    settings?: Record<string, unknown>;
}

export interface TenantDashboardStats {
    total_contents: number;
    published_contents: number;
    draft_contents: number;
    total_programs: number;
    programs_by_status: Record<string, number>;
    total_modules: number;
    total_content_types: number;
    total_tokens: number;
    total_portal_users: number;
    requests_today: number;
    content_by_type: { type_name: string; count: number }[];
    usage_trend: { date: string; requests: number; errors: number; sessions_created: number; sessions_submitted: number }[];
}

export interface ContentScoreStatsItem {
    content_id: string;
    content_title: string;
    content_type_name: string;
    session_count: number;
    avg_percentage: number;
    min_percentage: number;
    max_percentage: number;
    pass_count: number;
    fail_count: number;
}

export interface ContentScoreStatsTag {
    id: string;
    name: string;
    code: string;
    tag_type_name: string;
}

export interface ContentScoreStats {
    summary: {
        total_contents_with_sessions: number;
        total_sessions: number;
        avg_score_percentage: number;
        pass_rate: number;
    };
    items: ContentScoreStatsItem[];
    available_tags: ContentScoreStatsTag[];
}

export interface TenantThemeResponse {
    theme: Record<string, unknown> | null;
}

export interface TenantPortalUser {
    id: string;
    /** Django ``User.id`` (distinct from the membership ``id``); used to enroll in a program. */
    user_id: number | null;
    email: string;
    username: string;
    permission_group_id: string | null;
    permission_group_slug: string | null;
    permission_group_name: string | null;
    is_active: boolean;
    created_at: string;
}

export interface TenantPortalUserCreateRequest {
    username: string;
    password: string;
    email: string;
    permission_group_id: string;
}

export interface TenantPortalUserUpdateRequest {
    permission_group_id?: string;
    is_active?: boolean;
    password?: string;
}

export interface APIToken {
    id: string;
    name: string;
    description?: string;
    prefix: string;
    is_active: boolean;
    last_used_at: string | null;
    expires_at: string | null;
    created_at: string;
    /** Permission group UUID (wire field name matches DRF `permission_group`). */
    permission_group?: string | null;
    permission_group_slug?: string | null;
}

export interface ListParams {
    page?: number;
    page_size?: number;
    search?: string;
    is_active?: boolean;
    ordering?: string;
}

class TenantService {
    private basePath = '/tenants';

    /**
     * List all tenants
     */
    async list(params?: ListParams): Promise<TenantListResponse> {
        // Build query string from params
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.set('page', params.page.toString());
        if (params?.page_size) queryParams.set('page_size', params.page_size.toString());
        if (params?.search) queryParams.set('search', params.search);
        if (params?.is_active !== undefined)
            queryParams.set('is_active', params.is_active.toString());
        if (params?.ordering) queryParams.set('ordering', params.ordering);

        const queryString = queryParams.toString();
        const url = queryString ? `${this.basePath}/?${queryString}` : `${this.basePath}/`;

        const response = await apiClient.get<TenantListResponse>(url);
        return response.data;
    }

    /**
     * Get tenant by ID
     */
    async getById(id: string): Promise<Tenant> {
        const response = await apiClient.get<Tenant>(`${this.basePath}/${id}`);
        return response.data;
    }

    /**
     * Get aggregated dashboard statistics for a tenant.
     */
    async getDashboardStats(tenantId: string): Promise<TenantDashboardStats> {
        const response = await apiClient.get<TenantDashboardStats>(
            `${this.basePath}/${tenantId}/dashboard-stats`
        );
        return response.data;
    }

    /**
     * Get content score statistics for a tenant, optionally filtered by tags.
     */
    async getContentScoreStats(
        tenantId: string,
        params?: { tag_ids?: string[]; tag_type_id?: string }
    ): Promise<ContentScoreStats> {
        const searchParams = new URLSearchParams();
        if (params?.tag_ids?.length) searchParams.set('tag_ids', params.tag_ids.join(','));
        if (params?.tag_type_id) searchParams.set('tag_type_id', params.tag_type_id);
        const qs = searchParams.toString();
        const url = `${this.basePath}/${tenantId}/content-score-stats${qs ? `?${qs}` : ''}`;
        const response = await apiClient.get<ContentScoreStats>(url);
        return response.data;
    }

    /**
     * Current tenant for API token or tenant JWT (`GET /api/tenants/me`).
     */
    async getMe(): Promise<Tenant> {
        const response = await apiClient.get<Tenant>(`${this.basePath}/me`);
        return response.data;
    }

    /**
     * Current tenant theme only (API token or tenant JWT). Not staff admin GET /tenants/:id.
     */
    async getMyTheme(): Promise<TenantThemeResponse> {
        const response = await apiClient.get<TenantThemeResponse>(`${this.basePath}/me/theme`);
        return response.data;
    }

    /**
     * Replace the current tenant's theme (`PUT /api/tenants/me/theme`).
     *
     * EAA-1842: the theme editor used to save through `PATCH /api/tenants/me` with the
     * whole `settings` blob, so the write rode in on `tenant.self.write` — a code
     * sys-editor holds — and could not be gated apart from every other tenant setting.
     * This endpoint requires `tenant.theme.write` (sys-tenant-admin / sys-admin) and
     * writes only the `theme` key, so a concurrent change to an unrelated setting is
     * no longer clobbered by sending the merged blob back.
     */
    async updateMyTheme(theme: object | null): Promise<TenantThemeResponse> {
        const response = await apiClient.put<TenantThemeResponse>(
            `${this.basePath}/me/theme`,
            { theme }
        );
        return response.data;
    }

    /**
     * Update current tenant (API token or tenant JWT). Use instead of update(id) for portal.
     */
    async updateMyTenant(data: TenantUpdateRequest): Promise<Tenant> {
        const response = await apiClient.patch<Tenant>(`${this.basePath}/me`, data);
        return response.data;
    }

    /**
     * Register a new tenant
     */
    async register(data: TenantRegistrationRequest): Promise<TenantRegistrationResponse> {
        const response = await apiClient.post<TenantRegistrationResponse>(
            `${this.basePath}/register`,
            data
        );
        return response.data;
    }

    /**
     * Update tenant
     */
    async update(id: string, data: TenantUpdateRequest): Promise<Tenant> {
        const response = await apiClient.patch<Tenant>(`${this.basePath}/${id}`, data);
        return response.data;
    }

    /**
     * Delete tenant (deactivate)
     */
    async delete(id: string): Promise<void> {
        await apiClient.delete(`${this.basePath}/${id}`);
    }

    /**
     * Get tenant's API tokens
     */
    async getTokens(tenantId: string): Promise<APIToken[]> {
        const response = await apiClient.get<APIToken[]>(`${this.basePath}/${tenantId}/tokens`);
        return response.data;
    }

    /**
     * Update an API token (admin). Body uses DRF field `permission_group` (UUID or null).
     */
    async patchToken(
        tenantId: string,
        tokenId: string,
        data: Partial<Pick<APIToken, 'name' | 'description'>> & {
            permission_group?: string | null;
        }
    ): Promise<APIToken> {
        const response = await apiClient.patch<APIToken>(
            `${this.basePath}/${tenantId}/tokens/${tokenId}`,
            data
        );
        return response.data;
    }

    /**
     * Create a new API token for tenant
     */
    async createToken(
        tenantId: string,
        data: {
            name: string;
            permission_group_id: string;
            description?: string;
            expires_at?: string;
        }
    ): Promise<{ token: string; key: string }> {
        const response = await apiClient.post<{ token: unknown; key: string }>(
            `${this.basePath}/${tenantId}/tokens`,
            data
        );
        return { token: response.data.key, key: response.data.key };
    }

    /**
     * Regenerate an API token (returns new key once)
     */
    async regenerateToken(tenantId: string, tokenId: string): Promise<{ key: string }> {
        const response = await apiClient.post<{ key: string }>(
            `${this.basePath}/${tenantId}/tokens/${tokenId}/regenerate`
        );
        return response.data;
    }

    /**
     * Revoke an API token
     */
    async revokeToken(tenantId: string, tokenId: string): Promise<void> {
        await apiClient.delete(`${this.basePath}/${tenantId}/tokens/${tokenId}`);
    }

    async listPortalUsers(
        tenantId: string,
        params?: { permission?: string }
    ): Promise<TenantPortalUser[]> {
        const searchParams = new URLSearchParams();
        if (params?.permission) searchParams.set('permission', params.permission);
        const qs = searchParams.toString();
        const url = `${this.basePath}/${tenantId}/portal-users${qs ? `?${qs}` : ''}`;
        const response = await apiClient.get<TenantPortalUser[]>(url);
        return response.data;
    }

    async createPortalUser(
        tenantId: string,
        data: TenantPortalUserCreateRequest
    ): Promise<TenantPortalUser> {
        const response = await apiClient.post<TenantPortalUser>(
            `${this.basePath}/${tenantId}/portal-users`,
            data
        );
        return response.data;
    }

    async updatePortalUser(
        tenantId: string,
        membershipId: string,
        data: TenantPortalUserUpdateRequest
    ): Promise<TenantPortalUser> {
        const response = await apiClient.patch<TenantPortalUser>(
            `${this.basePath}/${tenantId}/portal-users/${membershipId}`,
            data
        );
        return response.data;
    }

    async deactivatePortalUser(tenantId: string, membershipId: string): Promise<void> {
        await apiClient.delete(`${this.basePath}/${tenantId}/portal-users/${membershipId}`);
    }
}

export const tenantService = new TenantService();
export default tenantService;
